import { useState } from "react";

import { Button, Callout, Intent } from "@blueprintjs/core";

import { Box } from "@/components/layout/flexbox";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import {
  ProgramComponentsQueryDocument,
  ProgramQueryDocument,
  useCloseProgramComponentMutation,
  useIsLastActiveComponentQuery,
} from "@/graphql";
import {
  LeadComponent,
  MainComponent,
  ParticipantComponent,
} from "@/types/programs";

import { ReleaseComponentOption } from "./ReleaseComponentOption";

interface CloseProgramComponentDialogProps {
  programId: string;
  open: boolean;
  onClose: () => void;
  component: MainComponent | LeadComponent | ParticipantComponent;
  onCloseComponent?: () => void;
}

export function CloseProgramComponentDialog({
  programId,
  open,
  onClose,
  component,
  onCloseComponent,
}: CloseProgramComponentDialogProps) {
  const [errorMessage, setErrorMessage] = useState<string>();

  const [isReleaseDependentComponents, setIsReleaseDependentComponents] =
    useState(true);

  const [closeProgramComponent, { loading }] = useCloseProgramComponentMutation(
    {
      onCompleted: (data) => {
        const { ok, msg } = data?.closeProgramComponent || {};
        if (ok) {
          onCloseComponent?.();
          onClose();
        } else {
          setErrorMessage(msg || "Something went wrong. Please try again.");
        }
      },
      onError: (err) => {
        setErrorMessage(err.message);
      },
      refetchQueries: [
        {
          query: ProgramQueryDocument,
          variables: {
            programId,
          },
        },
        {
          query: ProgramComponentsQueryDocument,
          variables: {
            programId,
          },
        },
      ],
    },
  );

  const {
    loading: checkLastActiveComponentLoading,
    data: isLastActiveComponentData,
  } = useIsLastActiveComponentQuery({
    variables: { programId, componentId: component?.id },
    skip: !open,
  });

  const completeProgramWarning =
    isLastActiveComponentData?.isLastActiveComponent &&
    "This is the last active component in the program. Ending this component will mark the program as complete.";

  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title="Are you sure you want to end early?"
      width="480px"
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setErrorMessage(undefined);
      }}
    >
      <Box p={24}>
        {errorMessage && (
          <Box mb={3}>
            <Callout icon={null} intent={Intent.DANGER}>
              {errorMessage}
            </Callout>
          </Box>
        )}
        {completeProgramWarning && (
          <Box mb={3}>
            <Callout icon={null} intent={Intent.WARNING}>
              {completeProgramWarning}
            </Callout>
          </Box>
        )}
        <Box>
          <b>
            Are you sure you want to end early{" "}
            {component.name ? `"${component.name}"` : "this"} component?
          </b>
          <ul className="list-disc pl-6" style={{ padding: "8px 16px" }}>
            <li>
              No further survey responses will be accepted. The component will
              be marked as complete. This is not reversible.
            </li>
            {"dependentComponents" in component &&
            component.dependentComponents?.length ? (
              <>
                <li style={{ marginTop: 8 }}>
                  <ReleaseComponentOption
                    warningTitle="Closing this component will affect others that depend on it"
                    component={component}
                    defaultValue={isReleaseDependentComponents}
                    onChange={(value) => {
                      setIsReleaseDependentComponents(value);
                    }}
                  />
                </li>
              </>
            ) : (
              <></>
            )}
          </ul>
        </Box>

        <Box mt={3}>
          <Button
            intent="primary"
            large={true}
            disabled={loading || checkLastActiveComponentLoading}
            onClick={() => {
              closeProgramComponent({
                variables: {
                  programId,
                  programComponentId: component?.id,
                  isReleaseDependentComponents,
                },
              });
            }}
            loading={loading || checkLastActiveComponentLoading}
          >
            End Now
          </Button>
          <Button
            large={true}
            disabled={loading}
            onClick={() => {
              onClose();
            }}
            style={{ marginLeft: 12 }}
          >
            Nevermind
          </Button>
        </Box>
      </Box>
    </StyledDialog>
  );
}
